import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'
import type { IdentityDocType } from '../identityCheckPoints'

export type { IdentityDocType } from '../identityCheckPoints'
export type IdentityCheckStatus = 'pending' | 'verified' | 'rejected'

// The AU/NZ "100 point" identity check total for one client on one matter —
// returned by listIdentityChecks alongside the checks themselves.
export interface IdentityCheckPointsSummary {
  totalPoints: number
  required: number
  hasPrimary: boolean
  met: boolean
}

export type IdentityCheckMethod = 'manual' | 'didit'

// A non-media snapshot of the full Didit report (see DiditReport below),
// persisted on the check row once verification reaches a terminal outcome —
// lets the list view show a name/document summary without a live report
// fetch. Photos are never persisted (short-lived signed URLs); use
// getIdentityCheckReport for those.
export type IdentityCheckReportSummary = Omit<
  DiditReport,
  'portraitImageUrl' | 'frontImageUrl' | 'backImageUrl' | 'fullFrontImageUrl' | 'fullBackImageUrl'
>

export interface IdentityCheck {
  id: string
  clientId: string
  matterId: string
  // Older manual-only rows predate this field, so it may be absent.
  method?: IdentityCheckMethod
  docType: IdentityDocType
  documentId: string | null
  status: IdentityCheckStatus
  // Server-computed (see backend/src/identity-check-points.ts's
  // pointsForCheck) — null unless status is 'verified'. Never recompute this
  // client-side: a verified Didit check is floored to 70 points even when
  // its docType placeholder never got backfilled, a rule only the backend
  // applies consistently.
  points: number | null
  notes: string | null
  reviewedBy: string | null
  reviewedAt: string | null
  createdAt: string
  // Didit-only fields (present when method === 'didit').
  provider?: string
  providerSessionId?: string
  providerStatus?: string | null
  verificationUrl?: string | null
  reportSummary?: IdentityCheckReportSummary | null
  // Present when docType === 'medicare-card' — the result (or failure) of
  // Didit's Australia Medicare database validation, set server-side by
  // buildMedicareCheckFields in backend/src/index.ts. `submitted` is always
  // present so a failed/unconfigured check still shows what was checked.
  medicareValidation?: MedicareValidationSummary | null
}

export interface MedicareValidationSummary {
  matchType?: string | null
  outcomeCode?: string | null
  fieldValidations?: {
    dateOfBirth: string | null
    fullName: string | null
    identificationNumber: string | null
  }
  submitted?: {
    medicareCardNumber: string
    dateOfBirth: string
    firstName: string
    lastName: string
  }
  // Present only when the automatic check couldn't run or Didit's call
  // failed outright — the check still lands 'pending' for manual review.
  error?: string
}

// The full per-document report from Didit's decision endpoint — see
// docs.didit.me/core-technology/id-verification/report-id-verification.
// Media fields are short-lived signed URLs; only ever fetched live via
// getIdentityCheckReport, never persisted.
export interface DiditReport {
  status: string | null
  documentType: string | null
  documentSubtype: string | null
  firstName: string | null
  lastName: string | null
  fullName: string | null
  dateOfBirth: string | null
  age: number | null
  gender: string | null
  nationality: string | null
  documentNumber: string | null
  personalNumber: string | null
  expirationDate: string | null
  dateOfIssue: string | null
  issuingState: string | null
  issuingStateName: string | null
  address: string | null
  formattedAddress: string | null
  placeOfBirth: string | null
  maritalStatus: string | null
  portraitImageUrl: string | null
  frontImageUrl: string | null
  backImageUrl: string | null
  fullFrontImageUrl: string | null
  fullBackImageUrl: string | null
  warnings: { risk: string | null; shortDescription: string | null; longDescription: string | null }[]
}

// Authenticated: tenant-side review of KYC/ID-verification for one person on
// one matter — starting a check (Didit or manual upload) now only happens
// client-side via the portal (see portalLinkService.ts's
// startPortalIdentityCheck), so this is list/update/report only. Mirrors
// documentService.ts/paymentService.ts conventions.
export const useIdentityCheckService = () => {
  const { token, authFetch } = useAuth()

  const listIdentityChecks = async (
    matterId: string,
    clientId: string,
  ): Promise<{ identityChecks: IdentityCheck[]; pointsSummary: IdentityCheckPointsSummary | null }> => {
    if (!token) return { identityChecks: [], pointsSummary: null }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/clients/${clientId}/identity-checks`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list identity checks: ${response.status}`)
    }

    return { identityChecks: data.identityChecks || [], pointsSummary: data.pointsSummary || null }
  }

  const updateIdentityCheck = async (
    matterId: string,
    checkId: string,
    input: { status: IdentityCheckStatus; notes?: string },
  ): Promise<IdentityCheck> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/identity-checks/${checkId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to update identity check: ${response.status}`)
    }

    return data.identityCheck
  }

  // Live full Didit decision/report (name, DOB, document details, photos).
  // Fetched fresh on every call — the media fields are short-lived signed
  // URLs, so there's nothing to cache.
  const getIdentityCheckReport = async (matterId: string, checkId: string): Promise<DiditReport> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/identity-checks/${checkId}/report`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to fetch verification report: ${response.status}`)
    }

    return data.report
  }

  return {
    listIdentityChecks,
    updateIdentityCheck,
    getIdentityCheckReport,
  }
}
